import { createContext, useContext } from "react";

import {
  type FieldPath,
  type FieldValues,
  type Path,
  type RegisterOptions,
  useController as rhfUseController,
  type UseControllerProps,
  useFormContext,
  type UseFormReturn,
} from "react-hook-form";

export const ScopeContext = createContext<string>("");
export const join = (a: string, b: string) => (a ? `${a}.${b}` : b);

export const mapNames = (scope: string, n: any) =>
  typeof n === "string" ? join(scope, n) : Array.isArray(n) ? n.map((x) => join(scope, x)) : n;

export function useScopedForm<T extends FieldValues>() {
  const methods = useFormContext<T>();
  const scope = useContext(ScopeContext);

  const register = (name: Path<T>, options?: RegisterOptions<T>) =>
    methods.register(mapNames(scope, name) as Path<T>, options);

  const errors = scope ? (methods.formState.errors[scope] ?? {}) : methods.formState.errors;

  const touchedFields = scope
    ? ((methods.formState.touchedFields as any)?.[scope as any] as typeof methods.formState.touchedFields)
    : methods.formState.touchedFields;

  const dirtyFields = scope
    ? ((methods.formState.dirtyFields as any)[scope as any] as typeof methods.formState.dirtyFields)
    : methods.formState.dirtyFields;

  const validatingFields = scope
    ? ((methods.formState.validatingFields as any)[scope as any] as typeof methods.formState.validatingFields)
    : methods.formState.validatingFields;

  return {
    ...methods,
    register,
    getValues: (n?: any) => (n ? methods.getValues(mapNames(scope, n)) : methods.getValues(scope as any)),
    setValue: (n: any, v: any, o?: any) => methods.setValue(mapNames(scope, n), v, o),
    watch: (n?: any, d?: any) => (n ? methods.watch(mapNames(scope, n), d) : methods.watch()),
    clearErrors: (n?: any) => methods.clearErrors(mapNames(scope, n)),
    trigger: (n?: any) => methods.trigger(mapNames(scope, n)),
    // handy when you need the fully-qualified name (e.g., for Controller):
    scopeName: (n: string) => mapNames(scope, n) as Path<T>,
    formState: {
      ...methods.formState,
      errors,
      touchedFields,
      dirtyFields,
      validatingFields,
    },
  } as UseFormReturn<T> & { scopeName: (n: string) => Path<T> };
}

export function useScopedController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>(props: UseControllerProps<TFieldValues, TName, TTransformedValues>) {
  const scope = useContext(ScopeContext);
  const ctx = useFormContext<TFieldValues>();
  // prefer explicitly provided control, else use context
  const control = (props as any).control ?? ctx.control;

  return rhfUseController<TFieldValues, TName, TTransformedValues>({
    ...props,
    // map the *local* name into the fully-qualified scoped path
    name: join(scope, props.name as unknown as string) as TName,
    control,
  });
}

export function useScopedName() {
  const scope = useContext(ScopeContext);
  return (name: string) => join(scope, name);
}
